import { getUserOccupation, useUser } from '~/modules/user';
import type { DirectConversation } from '../../../types';
import { isSelfParticipant } from '../../../utils';
import { ParticipantAvatar } from '../../avatar/ParticipantAvatar';
import { ChatBoxSettingsRemoveButton } from './ChatBoxSettingsRemoveButton';
import { ChatBoxSettingsMembersDropdown } from './ChatBoxSettingsMembersDropdown';

/**
 * The list of participants in the chat box conversation settings "members" tab.
 * @see {@link ChatBoxSettingsMembers}
 */
export function ChatBoxSettingsMembersList({
    conversation,
    isDisabled = false,
    onRemoveParticipant,
}: {
    conversation: DirectConversation;
    isDisabled?: boolean;
    onRemoveParticipant?: (participantID: string | number) => void;
}) {
    const user = useUser();
    const showRemoveButton =
        conversation.IsGroupChat && conversation.Participants.length > 2;

    return (
        <ul className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            {conversation.Participants.map((participant) => (
                <li
                    key={participant.ID}
                    className="group flex items-center gap-[8px] rounded-full p-[8px] hover:bg-white/50"
                >
                    <ParticipantAvatar
                        className="size-[36px]"
                        fallbackClassName="!text-[14px]"
                        participant={participant.Identity}
                    />
                    <div className="min-w-0 flex-1 space-y-[2px]">
                        <div className="truncate text-[14px] font-medium leading-tight">
                            {participant.Identity.__typename === 'Professional'
                                ? participant.Identity.Title
                                : participant.Identity.Name}
                            {isSelfParticipant(participant, user) && ' (you)'}
                        </div>
                        <div className="truncate text-[12px] leading-tight opacity-70">
                            {participant.Identity.__typename === 'Professional'
                                ? participant.Identity.PrimaryCategory
                                      ?.PrettyTitle
                                : getUserOccupation(participant.Identity)}
                        </div>
                    </div>
                    {showRemoveButton &&
                        !isSelfParticipant(participant, user) && (
                            <ChatBoxSettingsRemoveButton
                                className="invisible group-hover:visible"
                                participant={participant}
                                disabled={isDisabled}
                                onConfirm={() => {
                                    onRemoveParticipant?.(participant.ID);
                                }}
                            />
                        )}
                    {!isSelfParticipant(participant, user) && (
                        <ChatBoxSettingsMembersDropdown
                            className="invisible group-hover:visible"
                            conversationID={Number(conversation.ID)}
                            participant={participant}
                        />
                    )}
                </li>
            ))}
        </ul>
    );
}
